import type { AxiosRequestConfig } from 'axios'

import { decodeTokenData } from './authToken'

/**
 * Paths where the request must not be altered (no wallet binding / public auth).
 */
const SKIP_URL_SUBSTRINGS = ['/user/refresh-token', '/user/clear-auth-cookies']

function shouldSkipUrl(url: string): boolean {
  return SKIP_URL_SUBSTRINGS.some((frag) => url.includes(frag))
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    !(v instanceof FormData) &&
    !(v instanceof URLSearchParams)
  )
}

/** Session wallet from JWT payload (server-signed). Null if no token / no claim. */
export function trustedWalletFromSessionToken(): string | null {
  const tokenPayload = decodeTokenData() as Record<string, unknown> | null
  if (!tokenPayload) return null
  const wa = tokenPayload.walletAddress
  const wAlt = tokenPayload.wallet
  const w =
    (typeof wa === 'string' && wa.trim()) ||
    (typeof wAlt === 'string' && wAlt.trim()) ||
    ''
  return w || null
}

/**
 * Use JWT wallet when logged in; otherwise keep the caller value (e.g. pre-session flows).
 * Use for URL segments and dedupe keys where GET has no body for the axios interceptor to fix.
 */
export function resolveTrustedWalletForRequest(proposedWallet: string): string {
  return trustedWalletFromSessionToken() ?? proposedWallet
}

/**
 * Defense in depth: when a Bearer token is present, its payload `walletAddress` is the
 * session identity the backend signed. Overwrite any client-tampered `walletAddress` on
 * JSON bodies and query params so DevTools/Redux edits cannot impersonate another wallet.
 *
 * This does **not** replace server-side checks — the API must still verify the JWT and
 * authorize every mutation. It only blocks a common class of client-only spoofing.
 */
export function bindAuthenticatedWalletToAxiosRequest(config: AxiosRequestConfig): void {
  const url = String(config.url || '')
  if (shouldSkipUrl(url)) return

  const trusted = trustedWalletFromSessionToken()
  if (!trusted) return

  const data = config.data
  if (isPlainRecord(data) && 'walletAddress' in data) {
    const cur = data.walletAddress
    if (typeof cur === 'string' && cur !== trusted) {
      data.walletAddress = trusted
    }
  }

  const params = config.params
  if (isPlainRecord(params) && 'walletAddress' in params) {
    const cur = params.walletAddress
    if (typeof cur === 'string' && cur !== trusted) {
      params.walletAddress = trusted
    }
  }
}
